import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FileErrorDialogType,
  FileErrorModalComponent,
} from '../../modal-dialog/file-error-modal/file-error-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { IconName } from '@app/utils/enums/icon-enum';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { InventoryFileService } from '@app/services/inventory/inventory-file.service';
import { FileUploadErrors } from '@app/utils/constants/file-upload-error.constants';
import { FileUploadConstants } from '@app/utils/constants/file-upload.constant';

@Component({
  selector: 'app-upload-inventory-items',
  standalone: true,
  imports: [IconComponent, TranslatePipe, MatProgressBarModule],
  templateUrl: './upload-inventory-items.component.html',
  styleUrls: ['./upload-inventory-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadInventoryItemsComponent implements OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly inventoryFileService = inject(InventoryFileService);
  private readonly dialog = inject(MatDialog);

  private readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  protected readonly IconName = IconName;
  protected isHovering = false;
  protected readonly selectedFile = signal<File | null>(null);
  protected isSubmitting = signal(false);
  protected errorTextKey = signal('');

  public openFilePicker(): void {
    this.fileInput().nativeElement.click();
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    input.value = '';
    this.handleFile(file);
    this.errorTextKey.set('');
  }

  public unselectFile(): void {
    this.selectedFile.set(null);
    this.errorTextKey.set('');
  }

  public submitFile(): void {
    if (!this.selectedFile()) return;
    this.isSubmitting.set(true);
    this.inventoryFileService
      .uploadFile(this.selectedFile()!)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe({
        error: (err) => {
          this.errorTextKey.set('inventory.uploadComponent.error.uploadFailed');
          this.showError(FileUploadErrors.UPLOAD_FAILED.title, err?.error?.message || err.message);
        },
      });
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isHovering = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isHovering = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isHovering = false;
    const file = event.dataTransfer?.files[0];
    if (!file) return;
    this.handleFile(file);
  }

  private handleFile(file: File): void {
    if (!this.validateFile(file)) return;
    this.selectedFile.set(file);
  }

  private validateFile(file: File): boolean {
    if (file.size > FileUploadConstants.MAX_SIZE_BYTES) {
      this.showError(
        FileUploadErrors.FILE_TOO_LARGE.title,
        FileUploadErrors.FILE_TOO_LARGE.subtitle,
      );
      return false;
    }
    if (
      file.type !== FileUploadConstants.ALLOWED_MIME_TYPE &&
      !file.name.endsWith(FileUploadConstants.ALLOWED_EXTENSION)
    ) {
      this.errorTextKey.set('inventory.uploadComponent.error.invalidFormat');
      this.showError(
        FileUploadErrors.INVALID_FORMAT.title,
        FileUploadErrors.INVALID_FORMAT.subtitle,
      );
      return false;
    }
    return true;
  }

  private showError(titleKey: string, subtitleKey: string, message?: string): void {
    this.dialog.open(FileErrorModalComponent, {
      data: {
        titleKey,
        subtitleKey,
        message,
        type: FileErrorDialogType.Upload,
        actionButtonCallback: () => this.openFilePicker(),
      },
    });
  }

  public ngOnDestroy(): void {
    this.unselectFile();
  }
}
